import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { PlayIcon } from '@shared/ui';
import type { DayPlan } from '@shared/types';
import { joinWords, planCopy } from '../lib/planCopy';

interface TodayPlanCardProps {
  plan: DayPlan;
  /** Учить больше нечего (день засчитан, повторять нечего, новых нет) — кнопка спокойная. */
  calm: boolean;
  phone: boolean;
  onTrain: () => void;
}

/**
 * Верх карточки «Тренировка на сегодня» новой главной (макет владельца 2026-10-22): есть что повторить —
 * крупное число «21 слово на повторение», иначе — заголовок сводки дня; кнопка «Тренироваться» ведёт в список
 * модулей, а не запускает готовую тренировку (решение владельца 2026-10-21). Рамку и строку серии даёт `TodayCard`.
 */
export function TodayPlanCard({ plan, calm, phone, onTrain }: TodayPlanCardProps) {
  const { t, i18n } = useTranslation();
  const { title, sub } = planCopy(plan, t, (words) => joinWords(words, i18n.language));
  const big = plan.repeat > 0;

  const button = (
    <button
      type="button"
      // Подсветка после знакомства показывает вход в тренировку — это он.
      data-tour="training"
      onClick={onTrain}
      className={cn(
        'flex flex-none items-center justify-center gap-3 whitespace-nowrap font-semibold transition-colors',
        phone ? 'h-12 w-full rounded-pill text-[16px]' : 'h-[52px] rounded-pill px-7 text-[16px]',
        calm
          ? 'border border-solid border-line bg-card text-fg hover:bg-white/[0.04]'
          : 'bg-accent text-fg-inv hover:bg-accent-hi',
      )}
    >
      <PlayIcon className="h-3.5 w-3.5 flex-none" aria-hidden />
      {t('training.startShort')}
    </button>
  );

  const headline = big ? (
    <h2 className="flex flex-wrap items-baseline gap-x-2.5 font-serif font-medium tracking-[-0.01em] text-fg">
      <span className={cn('tabular-nums leading-none', phone ? 'text-[38px]' : 'text-[44px]')}>{plan.repeat}</span>
      <span className={cn('leading-tight', phone ? 'text-[18px]' : 'text-[20px]')}>{t('today.repeatBig', { count: plan.repeat })}</span>
    </h2>
  ) : (
    <h2 className={cn('font-serif font-medium tracking-[-0.01em] text-fg', phone ? 'text-[22px] leading-[28px]' : 'text-[26px] leading-[32px]')}>
      {title}
    </h2>
  );

  return (
    <div className={cn(phone ? 'flex flex-col gap-3 p-4' : 'flex items-center gap-8 px-8 py-7')}>
      <div className="min-w-0 flex-1">
        <div className="mb-2 text-[12px] font-semibold uppercase leading-4 tracking-[0.14em] text-muted">{t('today.kicker')}</div>
        {headline}
        {sub && <p className={cn('mt-2 text-muted', phone ? 'line-clamp-2 text-[13px] leading-[18px]' : 'text-[14px] leading-5')}>{sub}</p>}
      </div>
      {button}
    </div>
  );
}
